<script>
	// Returned goods: the customer is credited at the lowest price they paid for the model during the season.
	// Pick a customer, then a model: the lowest price comes from their Verifone invoices,
	// after the line discount and the invoice's general discount, before VAT.
	// The returned models are gathered in a list (with quantities) that is printed at the end.
	import { onMount } from 'svelte';
	import { push } from 'svelte-spa-router';
	import fetchData from '../../api.js';
	import UserStore from '../../stores/user';
	import { VAT_PERCENT } from '../../lib/format';
	import AppShell from '../../components/AppShell.svelte';
	import CustomerPicker from '../../components/CustomerPicker.svelte';
	import Icon from '../../components/Icon.svelte';
	import ReturnPrint from '../../components/ReturnPrint.svelte';

	const PERIODS = [
		{ label: 'חצי שנה', months: 6 },
		{ label: 'שנה', months: 12 },
		{ label: 'שנתיים', months: 24 }
	];
	// the customer being handled and each customer's list stay on this tablet, so a refresh loses nothing
	const CUSTOMER_KEY = 'agents_returns_customer';
	const LIST_KEY = 'agents_returns_list:';

	function monthsAgo(n) {
		const d = new Date();
		d.setMonth(d.getMonth() - n);
		return d.toISOString().slice(0, 10);
	}

	let customer = null;
	let picking = false;
	let from = monthsAgo(12);
	let loading = false;
	let progress = null;
	let error = '';
	let offline = false;
	let docs = 0;
	let lines = [];
	let q = '';
	let open = '';
	let requestNo = 0;
	let list = [];
	let showList = false;

	// ₪180.5 -> ₪180.50: credit prices always with agorot
	const price = (n) => '₪' + (Number(n) || 0).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	const day = (d) => (d ? d.slice(8, 10) + '/' + d.slice(5, 7) + '/' + d.slice(2, 4) : '');
	const units = (n) => (Number.isInteger(n) ? n : n.toFixed(1));
	const pct = (n) => (Number(n) ? Number(n) + '%' : '—');
	const round2 = (n) => Math.round(n * 100) / 100;
	const today = new Date().toLocaleDateString('he-IL');

	// one row per model: every sold line of it, cheapest first
	function group(all) {
		const map = new Map();
		for (const l of all) {
			const key = l.model || l.item_id;
			let m = map.get(key);
			if (!m) {
				m = { code: key, name: l.model_name || l.desc, lines: [], qty: 0, docs: new Set(), last: l.date, min: l };
				map.set(key, m);
			}
			m.lines.push(l);
			m.qty += l.qty;
			m.docs.add(l.doc_no);
			if (l.net < m.min.net) m.min = l;
			if (l.date > m.last) m.last = l.date;
		}
		return [...map.values()]
			.map((m) => ({ ...m, docs: m.docs.size, lines: m.lines.sort((a, b) => a.net - b.net || b.date.localeCompare(a.date)) }))
			.sort((a, b) => b.last.localeCompare(a.last));
	}

	$: models = group(lines);
	$: needle = q.trim().toLowerCase();
	$: shown = !needle
		? models
		: models
				.filter((m) => [m.code, m.name].some((v) => String(v || '').toLowerCase().includes(needle)) ||
					m.lines.some((l) => l.item_id.toLowerCase().startsWith(needle) || String(l.desc || '').toLowerCase().includes(needle)))
				.sort((a, b) => Number(b.code.toLowerCase().startsWith(needle)) - Number(a.code.toLowerCase().startsWith(needle)));

	$: listCodes = new Set(list.map((i) => i.code));
	$: listUnits = list.reduce((s, i) => s + i.qty, 0);
	$: listTotal = round2(list.reduce((s, i) => s + round2(i.qty * i.net), 0));
	$: listVat = round2((listTotal * VAT_PERCENT) / 100);

	function readList() {
		try {
			list = JSON.parse(localStorage.getItem(LIST_KEY + customer.CustomerNo) || '[]') || [];
		} catch (e) {
			list = [];
		}
	}

	function saveList() {
		try {
			if (list.length) localStorage.setItem(LIST_KEY + customer.CustomerNo, JSON.stringify(list));
			else localStorage.removeItem(LIST_KEY + customer.CustomerNo);
		} catch (e) {
			/* storage blocked: the list still works until the page is closed */
		}
	}

	function toggle(m) {
		if (listCodes.has(m.code)) {
			list = list.filter((i) => i.code !== m.code);
		} else {
			list = [...list, { code: m.code, name: m.name, qty: 1, net: m.min.net, doc_no: m.min.doc_no, date: m.min.date, bought: m.qty }];
		}
		saveList();
	}

	function setQty(code, value) {
		const qty = Math.max(1, Math.floor(Number(value)) || 1);
		list = list.map((i) => (i.code === code ? { ...i, qty } : i));
		saveList();
	}

	function remove(code) {
		list = list.filter((i) => i.code !== code);
		saveList();
		if (!list.length) showList = false;
	}

	function clearList() {
		if (!confirm('לנקות את כל רשימת ההחזרה של ' + customer.CustomerName + '?')) return;
		list = [];
		saveList();
		showList = false;
	}

	// after a new period is read, the list follows the lowest prices of that period
	function refreshPrices(all) {
		const byCode = new Map(group(all).map((m) => [m.code, m]));
		list = list.map((i) => {
			const m = byCode.get(i.code);
			return m ? { ...i, name: m.name, net: m.min.net, doc_no: m.min.doc_no, date: m.min.date, bought: m.qty } : i;
		});
		saveList();
	}

	async function load() {
		if (!customer) return;
		const mine = ++requestNo;
		loading = true;
		error = '';
		offline = false;
		lines = [];
		progress = null;
		open = '';
		// the server copies a few invoices per call; ask again until all are in
		for (let i = 0; i < 100; i++) {
			const res = await fetchData({ action: 'agents_returns_lookup', token: $UserStore.data, customer_no: customer.CustomerNo, from });
			if (mine !== requestNo) return;
			if (!res || Number(res.success) !== 1) {
				error = (res && res.msg) || 'אין חיבור לשרת. בדוק את האינטרנט ונסה שוב.';
				break;
			}
			progress = { docs: Number(res.docs) || 0, done: (Number(res.docs) || 0) - (Number(res.pending) || 0) };
			if (res.lines) {
				lines = res.lines;
				docs = Number(res.docs) || 0;
				offline = !!res.offline;
				refreshPrices(res.lines);
				break;
			}
		}
		loading = false;
	}

	function pick(c) {
		picking = false;
		customer = c;
		try { localStorage.setItem(CUSTOMER_KEY, JSON.stringify(c)); } catch (e) { /* storage blocked */ }
		q = '';
		readList();
		load();
	}

	function setPeriod(months) {
		from = monthsAgo(months);
		load();
	}

	onMount(() => {
		if (!$UserStore || !$UserStore.data) {
			push('/login');
			return;
		}
		let saved = null;
		try { saved = JSON.parse(localStorage.getItem(CUSTOMER_KEY) || 'null'); } catch (e) { saved = null; }
		if (saved && saved.CustomerNo) pick(saved);
		else picking = true;
	});
</script>

<AppShell title="החזרות סחורה">
	<div class="screen">
		<section class="panel">
			<div class="field">
				<span class="label">לקוח</span>
				{#if customer}
					<button class="customer" on:click={() => (picking = true)}>
						<Icon name="customer" size={22} />
						<b>{customer.CustomerName}</b>
						<small class="muted">#{customer.CustomerNo}</small>
						<span class="change">החלפה</span>
					</button>
				{:else}
					<button class="btn-primary" on:click={() => (picking = true)}><Icon name="customer" size={22} /> בחירת לקוח</button>
				{/if}
			</div>
			<div class="field">
				<span class="label">חשבוניות מתאריך</span>
				<div class="period">
					<div class="seg">
						{#each PERIODS as p}
							<button class:on={from === monthsAgo(p.months)} on:click={() => setPeriod(p.months)}>{p.label}</button>
						{/each}
					</div>
					<input type="date" bind:value={from} on:change={load} max={monthsAgo(0)} />
				</div>
			</div>
		</section>

		{#if !customer}
			<div class="empty-state"><b>בוחרים לקוח</b>ואז מחפשים דגם, ורואים את המחיר הנמוך ביותר שהלקוח שילם עליו.</div>
		{:else if loading}
			<div class="loading">
				<div class="spinner" />
				<b>קורא את החשבוניות של הלקוח מווריפון…</b>
				{#if progress && progress.docs}<span class="muted">{progress.done} מתוך {progress.docs} חשבוניות</span>{/if}
				<small class="muted">בפעם הראשונה זה לוקח קצת זמן. בפעם הבאה זה מיידי.</small>
			</div>
		{:else if error}
			<div class="alert-error">{error}</div>
			<button class="btn-ghost retry" on:click={load}>לנסות שוב</button>
		{:else}
			{#if offline}<div class="alert-warn">ווריפון לא ענה. מוצגות רק חשבוניות שכבר נקראו קודם, ייתכן שחסרות חשבוניות חדשות.</div>{/if}

			<label class="search model-search">
				<span class="icon"><Icon name="search" size={22} /></span>
				<input type="search" placeholder="דגם: מספר או שם, למשל AM24172 או ג'ינס" bind:value={q} />
				{#if q}<button class="clear" on:click={() => (q = '')} aria-label="ניקוי"><Icon name="close" size={20} /></button>{/if}
			</label>
			<p class="muted note">
				{docs} חשבוניות מ-{day(from)}, {models.length} דגמים. המחיר הוא ליחידה, אחרי הנחת השורה וההנחה הכללית של החשבונית, לפני מע״מ.
				לוחצים "לרשימה" על כל דגם שהלקוח מחזיר, ובסוף מדפיסים את הרשימה.
			</p>

			{#if !shown.length}
				<div class="empty-state">
					<b>{models.length ? 'אין דגם כזה בחשבוניות של הלקוח' : 'אין חשבוניות לתקופה הזו'}</b>
					{models.length ? 'אפשר לחפש לפי מספר דגם, מספר פריט או חלק מהשם.' : 'אפשר להרחיב את התקופה למעלה.'}
				</div>
			{:else}
				<div class="models">
					{#each shown as m (m.code)}
						{@const expanded = open === m.code || shown.length === 1}
						<div class="model" class:expanded class:added={listCodes.has(m.code)}>
							<div class="model-top">
								<button class="model-head" on:click={() => (open = open === m.code ? '' : m.code)}>
									<span class="who">
										<b dir="ltr">{m.code}</b>
										<span class="name">{m.name}</span>
										<small class="muted">נקנו {units(m.qty)} יח׳ ב-{m.docs} {m.docs === 1 ? 'חשבונית' : 'חשבוניות'} · אחרונה {day(m.last)}</small>
									</span>
									<span class="best">
										<small>המחיר הנמוך ביותר</small>
										<b>{price(m.min.net)}</b>
										<small class="muted">חשבונית {m.min.doc_no} · {day(m.min.date)}</small>
									</span>
									<span class="caret"><Icon name="down" size={22} /></span>
								</button>
								<button class="add" class:on={listCodes.has(m.code)} on:click={() => toggle(m)}>
									{#if listCodes.has(m.code)}<Icon name="check" size={22} /> ברשימה{:else}<Icon name="plus" size={22} /> לרשימה{/if}
								</button>
							</div>
							{#if expanded}
								<div class="lines">
									<div class="lhead">
										<span>תאריך</span><span>חשבונית</span><span>פריט</span><span>כמות</span><span>מחיר מחירון</span><span>הנחת שורה</span><span>הנחה כללית</span><span>נטו ליחידה</span>
									</div>
									{#each m.lines as l, i}
										<div class="lrow" class:low={i === 0}>
											<span>{day(l.date)}</span>
											<span>{l.doc_no}</span>
											<span dir="ltr" class="item">{l.item_id}</span>
											<span>{units(l.qty)}</span>
											<span>{price(l.list_price)}</span>
											<span>{pct(l.line_discount)}</span>
											<span>{pct(l.doc_discount)}</span>
											<span class="net">{price(l.net)}</span>
										</div>
									{/each}
								</div>
							{/if}
						</div>
					{/each}
				</div>
			{/if}
		{/if}

		{#if customer && list.length}
			<div class="list-spacer" />
			<button class="list-bar" on:click={() => (showList = true)}>
				<span class="count">{list.length}</span>
				<span class="what">
					<b>רשימת החזרה</b>
					<small>{list.length === 1 ? 'דגם אחד' : list.length + ' דגמים'} · {listUnits} יח׳ · {price(listTotal)} לפני מע״מ</small>
				</span>
				<span class="go">לרשימה והדפסה</span>
			</button>
		{/if}
	</div>

	{#if customer && list.length}
		<ReturnPrint
			company={$UserStore.company || ''}
			date={today}
			agent={$UserStore.agent_name || ''}
			customerName={customer.CustomerName}
			customerNo={customer.CustomerNo}
			items={list.map((i) => ({ code: i.code, name: i.name, qty: i.qty, price: i.net, doc_no: i.doc_no, date: i.date }))}
			note={'מחיר ליחידה: המחיר הנמוך ביותר ששילם הלקוח על הדגם בחשבוניות מ-' + day(from) + ', אחרי הנחת שורה והנחה כללית, לפני מע״מ.'}
		/>
	{/if}
</AppShell>

{#if picking}
	<CustomerPicker on:close={() => (picking = false)} on:select={(e) => pick(e.detail)} />
{/if}

{#if showList && customer}
	<div class="backdrop over-sheet" on:click={() => (showList = false)} />
	<div class="modal list-modal" role="dialog" aria-label="רשימת החזרה">
		<div class="modal-head">
			<h2>רשימת החזרה · {customer.CustomerName}</h2>
			<button class="icon-btn" on:click={() => (showList = false)} aria-label="סגירה"><Icon name="close" /></button>
		</div>
		<div class="modal-body">
			{#each list as it (it.code)}
				<div class="litem">
					<div class="lname">
						<b dir="ltr">{it.code}</b>
						<span>{it.name}</span>
						<small class="muted">{price(it.net)} ליחידה · חשבונית {it.doc_no}</small>
						{#if it.bought && it.qty > it.bought}<small class="over">בתקופה נקנו רק {units(it.bought)} יח׳</small>{/if}
					</div>
					<div class="stepper">
						<button on:click={() => setQty(it.code, it.qty - 1)} disabled={it.qty <= 1} aria-label="פחות"><Icon name="minus" size={20} /></button>
						<input type="number" inputmode="numeric" min="1" value={it.qty} on:change={(e) => setQty(it.code, e.target.value)} />
						<button on:click={() => setQty(it.code, it.qty + 1)} aria-label="עוד"><Icon name="plus" size={20} /></button>
					</div>
					<b class="lsum">{price(round2(it.qty * it.net))}</b>
					<button class="icon-btn" on:click={() => remove(it.code)} aria-label="הסרה מהרשימה"><Icon name="trash" size={20} /></button>
				</div>
			{/each}
			<div class="totals">
				<div><span>סה״כ לפני מע״מ ({listUnits} יח׳)</span><b>{price(listTotal)}</b></div>
				<div><span>מע״מ {VAT_PERCENT}%</span><span>{price(listVat)}</span></div>
				<div class="grand"><span>סה״כ לזיכוי כולל מע״מ</span><b>{price(round2(listTotal + listVat))}</b></div>
			</div>
		</div>
		<div class="foot">
			<button class="btn-danger-text" on:click={clearList}>ניקוי הרשימה</button>
			<span class="grow" />
			<button class="btn-ghost" on:click={() => (showList = false)}>הוספת דגמים</button>
			<button class="btn-primary" on:click={() => window.print()}>הדפסה</button>
		</div>
	</div>
{/if}

<style>
	.panel { display: flex; gap: 24px; flex-wrap: wrap; align-items: flex-end; background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); padding: 16px 20px; margin-bottom: 16px; }
	.field { display: flex; flex-direction: column; gap: 6px; }
	.label { font-size: 14px; color: var(--muted); }
	.customer { display: flex; align-items: center; gap: 10px; height: 56px; padding: 0 16px; border: 1px solid var(--line-strong); border-radius: 14px; background: var(--surface); font-size: 18px; max-width: 520px; }
	.customer b { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.change { margin-inline-start: 8px; color: var(--ink-2); text-decoration: underline; font-size: 15px; }
	.period { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
	.period input { height: 50px; border: 1px solid var(--line-strong); border-radius: 12px; padding: 0 12px; font-size: 16px; background: var(--surface); }
	.loading { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 20px; text-align: center; }
	.loading .spinner { margin: 20px auto 8px; }
	.retry { margin-top: 12px; }
	.alert-warn { background: var(--warn-bg); color: var(--warn); border-radius: 12px; padding: 12px 16px; margin-bottom: 12px; }
	.model-search { background: var(--surface); max-width: 640px; }
	.note { margin: 10px 0 16px; font-size: 15px; }
	.models { display: flex; flex-direction: column; gap: 10px; }
	.model { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); overflow: hidden; }
	.model.expanded { border-color: var(--line-strong); box-shadow: var(--shadow); }
	.model.added { border-color: var(--ok); box-shadow: inset 0 0 0 1px var(--ok); }
	.model-top { display: flex; align-items: stretch; }
	.model-head { flex: 1; min-width: 0; display: grid; grid-template-columns: 1fr auto 28px; gap: 16px; align-items: center; padding: 14px 20px; border: 0; background: none; text-align: start; }
	.add { flex: none; width: 130px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px; border: 0; border-inline-start: 1px solid var(--line); background: var(--bg); font-size: 15px; font-weight: 700; color: var(--ink); }
	.add.on { background: var(--ok-bg); color: var(--ok); }
	.who { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
	.who b { font-size: 19px; text-align: right; }
	.who .name { font-size: 16px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.who small { font-size: 14px; }
	.best { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; }
	.best b { font-size: 26px; font-weight: 800; color: var(--ok); }
	.best small { font-size: 13px; }
	.caret { color: var(--muted); transition: transform .15s; }
	.expanded .caret { transform: rotate(180deg); }
	.lines { border-top: 1px solid var(--line); font-size: 15px; overflow-x: auto; }
	.lhead, .lrow { display: grid; grid-template-columns: 80px 80px minmax(130px, 1fr) 56px 110px 90px 90px 110px; gap: 10px; align-items: center; padding: 0 20px; min-width: 820px; }
	.lhead { height: 40px; color: var(--muted); font-size: 13px; background: var(--bg); }
	.lrow { min-height: 46px; border-top: 1px solid var(--line); }
	.lrow.low { background: var(--ok-bg); }
	.lrow.low .net { color: var(--ok); }
	.item { text-align: right; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.net { font-weight: 800; }

	/* the list being gathered: a bar above the tabs, the full list in a window */
	.list-spacer { height: 96px; }
	.list-bar {
		position: fixed; z-index: 19; inset-inline: 0; margin: 0 auto; width: min(760px, calc(100vw - 32px));
		bottom: calc(var(--tabbar-h) + env(safe-area-inset-bottom) + 12px);
		display: flex; align-items: center; gap: 14px; height: 68px; padding: 0 12px 0 18px;
		border: 0; border-radius: 16px; background: var(--ink); color: #fff; box-shadow: var(--shadow); text-align: start;
	}
	.list-bar .count { width: 40px; height: 40px; border-radius: 50%; background: #fff; color: var(--ink); display: grid; place-items: center; font-weight: 800; font-size: 18px; flex: none; }
	.list-bar .what { flex: 1; min-width: 0; display: flex; flex-direction: column; }
	.list-bar .what b { font-size: 17px; }
	.list-bar .what small { font-size: 14px; opacity: .8; }
	.list-bar .go { background: #fff; color: var(--ink); border-radius: 12px; padding: 0 16px; height: 46px; display: grid; place-items: center; font-weight: 700; font-size: 16px; flex: none; }
	.list-modal { width: min(820px, 94vw); display: flex; flex-direction: column; }
	.litem { display: grid; grid-template-columns: 1fr auto 110px 48px; gap: 14px; align-items: center; padding: 12px 0; border-bottom: 1px solid var(--line); }
	.lname { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
	.lname b { text-align: right; font-size: 17px; }
	.lname span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.lname small { font-size: 13px; }
	.over { color: var(--warn); font-weight: 700; }
	.stepper { display: flex; align-items: center; border: 1px solid var(--line-strong); border-radius: 12px; overflow: hidden; }
	.stepper button { width: 44px; height: 46px; border: 0; background: var(--bg); display: grid; place-items: center; }
	.stepper button:disabled { opacity: .35; }
	.stepper input { width: 56px; height: 46px; border: 0; text-align: center; font-size: 18px; font-weight: 700; -moz-appearance: textfield; }
	.stepper input::-webkit-inner-spin-button { -webkit-appearance: none; }
	.lsum { text-align: left; font-size: 17px; }
	.totals { display: flex; flex-direction: column; gap: 6px; padding: 14px 0 4px; }
	.totals div { display: flex; justify-content: space-between; font-size: 16px; }
	.totals .grand { font-size: 19px; border-top: 1px solid var(--line); padding-top: 8px; }
	.foot { display: flex; align-items: center; gap: 10px; padding: 14px 20px; border-top: 1px solid var(--line); }
	.grow { flex: 1; }

</style>
